import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";

const NAV_LINKS = [
  { to: "/", label: "Home", testId: "nav-home" },
];

export const NavBar = () => {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Prevent body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const desktopLink = (to, label, testId) => (
    <Link
      key={to}
      to={to}
      data-testid={testId}
      className={`font-mono-overline transition-colors duration-300 ${
        pathname === to ? "text-[#e2a9f1]" : "text-[#A3A3A3] hover:text-[#EDEDED]"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5"
      data-testid="site-navbar"
    >
      {/* DESKTOP — logo left, menu right */}
      <div className="hidden md:flex max-w-7xl mx-auto px-6 lg:px-12 h-40 md:h-44 items-center justify-between">
        <Link to="/" data-testid="nav-home-link-desktop" className="block">
          <img
            src="/swingle-logo.png"
            alt="Swingle Events"
            className="h-36 md:h-40 w-auto"
            data-testid="nav-logo"
          />
        </Link>
        <nav className="flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map((l) => desktopLink(l.to, l.label, l.testId))}
          <a
            href="#final-cta"
            data-testid="nav-cta-waitlist"
            className="inline-flex items-center bg-[#e2a9f1] text-[#0A0A0A] font-semibold tracking-wide hover:bg-[#efc8f7] transition-colors duration-300 rounded-sm px-5 py-2 text-sm"
          >
            Join Waitlist
          </a>
        </nav>
      </div>

      {/* MOBILE — compact bar with logo + burger */}
      <div className="md:hidden flex items-center justify-between px-5 h-36">
        <Link to="/" data-testid="nav-home-link-mobile" className="block">
          <img
            src="/swingle-logo.png"
            alt="Swingle Events"
            className="h-32 w-auto"
            data-testid="nav-logo-mobile"
          />
        </Link>
        <button
          type="button"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          data-testid="mobile-menu-toggle"
          className="p-2 -mr-2 text-[#EDEDED] hover:text-[#e2a9f1] transition-colors"
        >
          {mobileOpen ? <X size={28} weight="thin" /> : <List size={28} weight="thin" />}
        </button>
      </div>

      {/* MOBILE DRAWER */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile-drawer"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="md:hidden absolute top-full left-0 right-0 bg-[#0A0A0A] border-b border-[#2A2A2A]"
            data-testid="mobile-drawer"
          >
            <nav className="flex flex-col px-6 py-6 gap-1">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.testId}-mobile`}
                  className={`py-3 text-lg font-serif-display border-b border-[#1A1A1A] last:border-b-0 transition-colors ${
                    pathname === l.to ? "text-[#e2a9f1]" : "text-[#EDEDED] hover:text-[#e2a9f1]"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
              <a
                href="#final-cta"
                data-testid="nav-cta-waitlist-mobile"
                className="mt-4 inline-flex items-center justify-center bg-[#e2a9f1] text-[#0A0A0A] font-semibold tracking-wide hover:bg-[#efc8f7] transition-colors duration-300 rounded-sm px-5 py-3 text-sm"
              >
                Join Waitlist
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export const Footer = () => (
  <footer className="border-t border-[#2A2A2A] mt-32 py-12 px-6 lg:px-12" data-testid="site-footer">
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      <div>
        <img
          src="/swingle-logo.png"
          alt="Swingle Events"
          className="h-28 w-auto"
          data-testid="footer-logo"
        />
        <div className="font-mono-overline text-[#A3A3A3] mt-3">
          Discreet · Curated · 21+
        </div>
      </div>
      <div className="text-sm text-[#A3A3A3] max-w-md leading-relaxed">
        Swingle is a beautifully designed app for UK Swingers. 100% ID-verified profiles
        that needs to be completed every 6 months, offender-checked members and a proper
        messaging experience — no more bouncing between multiple apps to meet real people like
        you.
      </div>
      <div className="flex flex-col gap-2 font-mono-overline text-[#A3A3A3]">
        <a href="#final-cta" data-testid="footer-waitlist-link" className="hover:text-[#e2a9f1] transition-colors">
          Join Waitlist
        </a>
        <div>© {new Date().getFullYear()} Swingle</div>
      </div>
    </div>
  </footer>
);

export const PageShell = ({ children }) => (
  <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED]">
    <NavBar />
    <main className="pt-36 md:pt-44">{children}</main>
    <Footer />
  </div>
);
